import { useWsmOptionSetCreateMutation, useWsmOptionSetListQuery } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";

import WsmOptionSetDetailsPage from "../components/WsmOptionSetDetailsPage/WsmOptionSetDetailsPage";
import { getWsmIndexedErrors } from "../errors";
import { sectionMessages } from "../messages";
import { readWsmProductPrefill, wsmOptionSetUrl } from "../urls";
import { useTierGroups } from "../useTierGroups";
import {
  defaultOptionSetPosition,
  emptyOptionSetFormData,
  getOptionSetCreateInput,
} from "../utils";

/** Enough to cover any product's question list; no product carries a hundred. */
const SIBLINGS_PAGE = 100;

export const WsmOptionSetCreateView = () => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  // Opened from a product page, this screen already knows its product; that
  // product is also the one a new question is placed among. A shared question
  // still has to land somewhere on the page it is added from.
  const prefill = readWsmProductPrefill(useLocation().search);
  const [product, setProduct] = useState(prefill?.id ?? "");
  const tierGroups = useTierGroups();
  // What the product is already asking. A blank Position used to save as 0 and
  // put the new question in front of every one of them, on the list and in the
  // storefront payload, so the default is computed from these.
  const { data: siblingData } = useWsmOptionSetListQuery({
    variables: { first: SIBLINGS_PAGE, filter: { product } },
    skip: !product,
    fetchPolicy: "cache-and-network",
  });
  const suggestedSortOrder = defaultOptionSetPosition(
    (mapEdgesToItems(siblingData?.wsmOptionSets) ?? []).map(optionSet => optionSet.sortOrder),
  );
  const [createOptionSet, createOptionSetOpts] = useWsmOptionSetCreateMutation({
    onCompleted: result => {
      const optionSet = result.wsmOptionSetCreate?.optionSet;

      if (optionSet && !result.wsmOptionSetCreate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmOptionSetUrl(optionSet.id));
      }
    },
  });
  const errors = createOptionSetOpts.data?.wsmOptionSetCreate?.errors ?? [];

  return (
    <WsmOptionSetDetailsPage
      title={intl.formatMessage(sectionMessages.optionSetCreateTitle)}
      initialData={{
        ...emptyOptionSetFormData(),
        products: prefill ? [{ id: prefill.id, name: prefill.name || prefill.id }] : [],
      }}
      suggestedSortOrder={suggestedSortOrder}
      errors={errors}
      valueRowErrors={getWsmIndexedErrors(errors, "values")}
      tierGroups={tierGroups}
      disabled={createOptionSetOpts.loading}
      saveButtonBarState={getMutationState(
        createOptionSetOpts.called,
        createOptionSetOpts.loading,
        errors,
      )}
      onProductsChange={products => setProduct(products[0]?.id ?? "")}
      onSubmit={formData =>
        createOptionSet({ variables: { input: getOptionSetCreateInput(formData) } })
      }
    />
  );
};

export default WsmOptionSetCreateView;
